/**
 * Grid + axis painting.
 *
 * Grid and axis lines are recessive by construction — they take their colour
 * from `theme.grid` / `theme.axis`, which are low-alpha ink, and they are never
 * given a series hue. Axis text uses `theme.textDim`, never a series colour:
 * identity is carried by the coloured mark, not by coloured words.
 */

import { formatTick } from "./charts-scale.mjs";
import type { BandScale } from "./charts-scale.mjs";
import { crisp } from "./layers";
import type { ChartTheme, Rect } from "./types";

export const AXIS_FONT_PX = 11;

export interface ValueAxisSpec {
  ticks: number[];
  step: number;
  valueAt: (v: number) => number;
  format?: (value: number, index: number) => string;
  label?: string;
}

export interface PositionAxisSpec {
  /** Category labels when the axis is banded. */
  labels?: string[];
  band?: BandScale | null;
  ticks?: number[];
  step?: number;
  posAt?: (v: number) => number;
  format?: (value: number, index: number) => string;
  label?: string;
}

/** Widest value-axis label, so the plot rect can be reserved before drawing. */
export function measureValueAxis(
  g: CanvasRenderingContext2D,
  spec: { ticks: number[]; step: number; format?: (v: number, i: number) => string },
  theme: ChartTheme
): number {
  g.font = `${AXIS_FONT_PX}px ${theme.fontUi}`;
  let w = 0;
  spec.ticks.forEach((t, i) => {
    const text = spec.format ? spec.format(t, i) : formatTick(t, spec.step);
    w = Math.max(w, g.measureText(text).width);
  });
  return Math.ceil(w);
}

export function drawValueAxis(
  g: CanvasRenderingContext2D,
  plot: Rect,
  spec: ValueAxisSpec,
  theme: ChartTheme,
  orientation: "vertical" | "horizontal"
) {
  g.font = `${AXIS_FONT_PX}px ${theme.fontUi}`;
  g.lineWidth = 1;

  if (orientation === "horizontal") {
    // Value runs along x; gridlines are vertical.
    g.textAlign = "center";
    g.textBaseline = "top";
    spec.ticks.forEach((t, i) => {
      const x = crisp(spec.valueAt(t));
      g.strokeStyle = t === 0 ? theme.axis : theme.grid;
      g.beginPath();
      g.moveTo(x, plot.y);
      g.lineTo(x, plot.y + plot.height);
      g.stroke();
      g.fillStyle = theme.textDim;
      g.fillText(
        spec.format ? spec.format(t, i) : formatTick(t, spec.step),
        x,
        plot.y + plot.height + 6
      );
    });
    return;
  }

  g.textAlign = "right";
  g.textBaseline = "middle";
  spec.ticks.forEach((t, i) => {
    const y = crisp(spec.valueAt(t));
    g.strokeStyle = t === 0 ? theme.axis : theme.grid;
    g.beginPath();
    g.moveTo(plot.x, y);
    g.lineTo(plot.x + plot.width, y);
    g.stroke();
    g.fillStyle = theme.textDim;
    g.fillText(spec.format ? spec.format(t, i) : formatTick(t, spec.step), plot.x - 8, y);
  });
}

/**
 * Category / position axis. Labels that would collide are dropped by stride
 * rather than rotated or truncated — a half-drawn label is worse than none, and
 * the table view carries the full names anyway.
 */
export function drawPositionAxis(
  g: CanvasRenderingContext2D,
  plot: Rect,
  spec: PositionAxisSpec,
  theme: ChartTheme,
  orientation: "vertical" | "horizontal"
) {
  g.font = `${AXIS_FONT_PX}px ${theme.fontUi}`;
  g.fillStyle = theme.textDim;
  g.strokeStyle = theme.axis;
  g.lineWidth = 1;

  const horizontal = orientation === "horizontal";

  if (spec.labels && spec.band) {
    const band = spec.band;
    const labels = spec.labels;
    if (horizontal) {
      g.textAlign = "right";
      g.textBaseline = "middle";
      const stride = Math.max(1, Math.ceil((labels.length * 14) / Math.max(1, plot.height)));
      labels.forEach((text, i) => {
        if (i % stride) return;
        g.fillText(clip(g, text, plot.x - 10), plot.x - 8, band.center(i));
      });
    } else {
      g.textAlign = "center";
      g.textBaseline = "top";
      const widest = labels.reduce((m, t) => Math.max(m, g.measureText(t).width), 0);
      const stride = Math.max(1, Math.ceil(((widest + 12) * labels.length) / Math.max(1, plot.width)));
      labels.forEach((text, i) => {
        if (i % stride) return;
        g.fillText(clip(g, text, band.step * stride), band.center(i), plot.y + plot.height + 6);
      });
    }
    return;
  }

  if (spec.ticks && spec.posAt) {
    const posAt = spec.posAt;
    const step = spec.step || 1;
    g.textAlign = "center";
    g.textBaseline = "top";
    spec.ticks.forEach((t, i) => {
      const text = spec.format ? spec.format(t, i) : formatTick(t, step);
      g.fillText(text, posAt(t), plot.y + plot.height + 6);
    });
  }
}

/** The axis rule itself — one recessive line along the baseline. */
export function drawAxisRule(g: CanvasRenderingContext2D, plot: Rect, theme: ChartTheme) {
  g.strokeStyle = theme.axis;
  g.lineWidth = 1;
  g.beginPath();
  g.moveTo(plot.x, crisp(plot.y + plot.height));
  g.lineTo(plot.x + plot.width, crisp(plot.y + plot.height));
  g.stroke();
}

/** Ellipsise to fit; returns "" when there is no room for even an ellipsis. */
export function clip(g: CanvasRenderingContext2D, text: string, maxWidth: number): string {
  if (maxWidth <= 0) return "";
  if (g.measureText(text).width <= maxWidth) return text;
  let out = text;
  while (out.length > 1 && g.measureText(out + "…").width > maxWidth) out = out.slice(0, -1);
  return out.length > 1 ? out + "…" : "";
}
